import React, { useEffect, useState } from "react";
import { QuillLogo } from "../components/QuillLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INK = "#1C1A17";
const PAPER = "#F4F1EA";
const PAPER_DEEP = "#ECE7DC";
const OXBLOOD = "#7A2E2E";
const OXBLOOD_WARM = "#B0573F";
const STONE = "#A8A296";
const SLATE = "#6E6A5E";

const body = { fontFamily: "Lato, system-ui, sans-serif" };

const SA_PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "North West", "Northern Cape", "Western Cape",
];

// SADC member states we courier to (South Africa uses the domestic option).
const SADC_COUNTRIES = [
  "Angola", "Botswana", "Comoros", "Democratic Republic of the Congo", "Eswatini",
  "Lesotho", "Madagascar", "Malawi", "Mauritius", "Mozambique", "Namibia",
  "Seychelles", "Tanzania", "Zambia", "Zimbabwe",
];

// Dialing codes: SADC region first, then a few common international codes.
const DIAL_CODES = [
  "+27", "+264", "+267", "+263", "+260", "+258", "+266", "+268", "+265",
  "+244", "+255", "+243", "+261", "+230", "+248", "+269", "+44", "+1",
];

const fieldStyle = { ...body, borderBottom: `1px solid ${STONE}`, color: INK, backgroundColor: "transparent" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase font-serif mb-2" style={{ letterSpacing: "0.18em", color: SLATE }}>{label}</span>
    {children}
  </label>
);

const money = (v) => `R${Number(v).toFixed(2)}`;

const BookCheckout = () => {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", dial_code: "+27", quantity: 1, delivery_method: "courier_sa",
    address: "", suburb: "", city: "", province: "", country: "", postal_code: "", note: "",
  });

  // Honeypot kept OUT of the user form state (and in a display:none wrapper with an
  // uncommon name) so password managers / browser autofill cannot populate it and
  // falsely trip the anti-bot check. Real visitors leave it blank.
  const [winnow, setWinnow] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Checkout · No One Is Coming To Save You";
    fetch(`${API}/book/config`).then((r) => r.json()).then(setConfig).catch(() => setConfig(null));
    if (new URLSearchParams(window.location.search).get("cancelled")) {
      setMsg("Your payment was cancelled. Your details are below if you would like to try again.");
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isCourier = form.delivery_method.startsWith("courier");
  const isSadc = form.delivery_method === "courier_sadc";

  const bookPrice = config && config.book_price ? Number(config.book_price) : 0;
  const feeSa = config && config.delivery_fee_sa ? Number(config.delivery_fee_sa) : 0;
  const feeSadc = config && config.delivery_fee_sadc ? Number(config.delivery_fee_sadc) : 0;
  const qty = Math.max(1, Number(form.quantity) || 1);
  const subtotal = bookPrice * qty;
  const delivery = form.delivery_method === "courier_sa" ? feeSa : form.delivery_method === "courier_sadc" ? feeSadc : 0;
  const total = subtotal + delivery;
  const preorder = config ? config.preorder : true;
  const releaseDate = config ? config.release_date : "1 December 2026";

  const pay = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const postCheckout = () => fetch(`${API}/book/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, quantity: qty, mobile: `${form.dial_code} ${form.mobile}`.trim(), company: winnow }),
      });
      let res;
      try {
        res = await postCheckout();
      } catch {
        // transient network blip (e.g. a brief server restart): retry once
        await new Promise((r) => setTimeout(r, 1200));
        res = await postCheckout();
      }
      if (res.status === 503) {
        setMsg("Direct ordering is being set up. Please use a retailer, or write to us at info@theravensquill.co.za.");
        return;
      }
      if (res.status === 422) {
        setMsg("Please complete all required delivery fields.");
        return;
      }
      if (res.status === 400) {
        setMsg("We couldn't verify the form. Please refresh the page and try again, or email info@theravensquill.co.za.");
        return;
      }
      if (!res.ok) throw new Error("checkout failed");
      const { process_url, fields } = await res.json();
      const f = document.createElement("form");
      f.method = "POST";
      f.action = process_url;
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        f.appendChild(input);
      });
      document.body.appendChild(f);
      f.submit();
    } catch {
      setMsg("We could not open checkout just now. Please try again, or write to us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ backgroundColor: PAPER, color: INK, minHeight: "100vh" }} data-testid="book-checkout-page">
      {/* header band */}
      <div style={{ backgroundColor: "#120C08" }} className="py-8">
        <div className="mx-auto max-w-[920px] px-6 flex items-center justify-between">
          <a href="/no-one-is-coming" className="flex items-center gap-3" data-testid="checkout-back-link">
            <QuillLogo height={38} variant="gold" />
            <span className="font-serif italic text-[18px]" style={{ color: PAPER }}>The Raven&rsquo;s Quill</span>
          </a>
          {preorder && (
            <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.2em", color: OXBLOOD_WARM }}>
              Pre-order
            </span>
          )}
        </div>
      </div>

      {preorder && (
        <div style={{ backgroundColor: OXBLOOD }} data-testid="checkout-preorder-banner">
          <p className="mx-auto max-w-[920px] px-6 py-3 text-[13px] text-center" style={{ ...body, color: PAPER }}>
            This is a pre-order. Payment is taken now. Your signed book is despatched from {releaseDate}.
          </p>
        </div>
      )}

      <form onSubmit={pay} className="mx-auto max-w-[920px] px-6 py-14 grid md:grid-cols-[1fr_340px] gap-12" data-testid="checkout-form">
        {/* LEFT: details */}
        <div>
          <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.25em", color: OXBLOOD }}>Your details</span>
          <span className="mt-4 block" style={{ width: 64, height: "1px", backgroundColor: OXBLOOD }} />

          {msg && (
            <p className="mt-6 text-[14px] font-serif italic" style={{ color: OXBLOOD }} data-testid="checkout-message">{msg}</p>
          )}

          <div className="mt-8 grid gap-6">
            <Field label="Full name">
              <input required value={form.full_name} onChange={set("full_name")} data-testid="checkout-name" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Email">
                <input required type="email" value={form.email} onChange={set("email")} data-testid="checkout-email" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
              </Field>
              <Field label="Mobile number">
                <div className="flex items-center gap-2">
                  <select value={form.dial_code} onChange={set("dial_code")} data-testid="checkout-dialcode" className="py-2.5 outline-none rounded-none text-[16px] shrink-0" style={{ ...fieldStyle, maxWidth: "92px" }}>
                    {DIAL_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input required value={form.mobile} onChange={set("mobile")} data-testid="checkout-mobile" placeholder="72 123 4567" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
                </div>
              </Field>
            </div>

            {/* honeypot: hidden from humans and from autofill; bots that fill everything trip it */}
            <div style={{ display: "none" }} aria-hidden="true">
              <input type="text" tabIndex={-1} autoComplete="new-password" name="winnow_ref_xq" value={winnow} onChange={(e) => setWinnow(e.target.value)} />
            </div>

            <Field label="Quantity">
              <div className="flex items-center gap-4 mt-1">
                <button type="button" onClick={() => setForm((f) => ({ ...f, quantity: Math.max(1, (Number(f.quantity) || 1) - 1) }))} data-testid="checkout-qty-minus" className="w-9 h-9 border text-[18px] leading-none" style={{ borderColor: STONE, color: INK }}>&minus;</button>
                <span className="text-[18px] font-serif w-8 text-center" data-testid="checkout-qty-value">{qty}</span>
                <button type="button" onClick={() => setForm((f) => ({ ...f, quantity: Math.min(99, (Number(f.quantity) || 1) + 1) }))} data-testid="checkout-qty-plus" className="w-9 h-9 border text-[18px] leading-none" style={{ borderColor: STONE, color: INK }}>+</button>
              </div>
            </Field>
          </div>

          <div className="mt-12">
            <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.25em", color: OXBLOOD }}>Delivery</span>
            <span className="mt-4 block" style={{ width: 64, height: "1px", backgroundColor: OXBLOOD }} />
            <div className="mt-6 grid gap-3">
              {[
                { id: "courier_sa", title: "Courier to your door, anywhere in SA (R99)", sub: "Door to door, flat fee for the whole order." },
                { id: "courier_sadc", title: "Courier to the SADC region (R450)", sub: "Flat regional fee for the whole order." },
                { id: "collect", title: "Collect from Centurion (free)", sub: "We will email you to arrange a time." },
              ].map((o) => (
                <label key={o.id} data-testid={`delivery-${o.id}`} className="flex items-start gap-3 p-4 cursor-pointer transition-colors" style={{ border: `1px solid ${form.delivery_method === o.id ? OXBLOOD : STONE}`, backgroundColor: form.delivery_method === o.id ? "rgba(122,46,46,0.04)" : "transparent" }}>
                  <input type="radio" name="delivery" value={o.id} checked={form.delivery_method === o.id} onChange={() => setForm((f) => ({ ...f, delivery_method: o.id }))} className="mt-1 accent-[#7A2E2E]" />
                  <span>
                    <span className="block font-serif text-[16px]" style={{ color: INK }}>{o.title}</span>
                    <span className="block text-[13px] mt-1" style={{ ...body, color: SLATE }}>{o.sub}</span>
                  </span>
                </label>
              ))}
            </div>

            {isCourier && (
              <div className="mt-8 grid gap-6" data-testid="courier-address-fields">
                <Field label="Delivery address">
                  <input required={isCourier} value={form.address} onChange={set("address")} data-testid="checkout-address" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Suburb">
                    <input required={isCourier} value={form.suburb} onChange={set("suburb")} data-testid="checkout-suburb" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
                  </Field>
                  <Field label="City / town">
                    <input required={isCourier} value={form.city} onChange={set("city")} data-testid="checkout-city" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  {isSadc ? (
                    <Field label="Country">
                      <select required={isSadc} value={form.country} onChange={set("country")} data-testid="checkout-country" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle}>
                        <option value="">Select country</option>
                        {SADC_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Province">
                      <select required={isCourier && !isSadc} value={form.province} onChange={set("province")} data-testid="checkout-province" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle}>
                        <option value="">Select province</option>
                        {SA_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </Field>
                  )}
                  <Field label="Postal code">
                    <input required={isCourier} value={form.postal_code} onChange={set("postal_code")} data-testid="checkout-postal" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
                  </Field>
                </div>
              </div>
            )}

            <div className="mt-8">
              <Field label="Anything I should know, or who to personalise the signing to? (optional)">
                <textarea value={form.note} onChange={set("note")} rows={3} data-testid="checkout-note" className="w-full py-2.5 outline-none rounded-none text-[16px] resize-none" style={{ ...fieldStyle, border: `1px solid ${STONE}`, borderBottom: `1px solid ${STONE}`, padding: "10px" }} />
              </Field>
            </div>
          </div>
        </div>

        {/* RIGHT: summary */}
        <div>
          <div className="md:sticky md:top-8 p-7" style={{ backgroundColor: PAPER_DEEP }} data-testid="order-summary">
            <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.25em", color: OXBLOOD }}>Order summary</span>
            <span className="mt-4 block" style={{ width: 48, height: "1px", backgroundColor: OXBLOOD }} />

            <div className="mt-6 space-y-3 text-[15px]" style={body}>
              <div className="flex justify-between">
                <span>No One Is Coming To Save You &times; {qty}</span>
                <span data-testid="summary-subtotal">{money(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery</span>
                <span data-testid="summary-delivery">{isCourier ? money(delivery) : "Free, collection"}</span>
              </div>
              <div className="flex justify-between pt-3 text-[17px] font-serif" style={{ borderTop: `1px solid ${STONE}` }}>
                <span>Total</span>
                <span data-testid="summary-total">{money(total)}</span>
              </div>
            </div>

            <p className="mt-5 text-[12px]" style={{ ...body, color: SLATE }}>Signed paperback. Secure payment by PayFast.</p>

            {preorder && (
              <p className="mt-4 text-[13px] font-serif" style={{ color: INK }} data-testid="summary-preorder-line">
                Pre-order, ships from {releaseDate}.
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !config}
              data-testid="pay-now-button"
              className="mt-4 w-full px-6 py-3.5 text-[12px] uppercase font-serif transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ letterSpacing: "0.2em", backgroundColor: OXBLOOD, color: PAPER }}
            >
              {busy ? "Opening PayFast" : "Pay now with PayFast"}
            </button>

            {preorder && (
              <p className="mt-4 text-[11px]" style={{ ...body, color: SLATE }} data-testid="checkout-refund-note">
                Changed your mind before despatch? Email{" "}
                <a href="mailto:hello@theravensquill.co.za?subject=Pre-order%20refund%3A%20No%20One%20Is%20Coming%20To%20Save%20You" style={{ color: OXBLOOD }}>hello@theravensquill.co.za</a>{" "}
                for a full refund any time before your book ships.
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default BookCheckout;
